import { api } from '@/services/api'
import type { SubmitIdeaFormState } from '@/lib/formRecordsStorage'
import {
  CommentsListResponse as CommentsListResponseSchema,
  DashboardResponse as DashboardResponseSchema,
  HomeResponse as HomeResponseSchema,
  IdeasListResponse as IdeasListResponseSchema,
  IdeaDetailResponse as IdeaDetailResponseSchema,
  ToggleUpvoteResponse as ToggleUpvoteResponseSchema,
} from '@/shared-api'
import type { z } from 'zod'

// Runs the response through the shared schema. Drift logs prominently to
// the console so it's visible in DevTools, but does NOT throw — the UI
// keeps working even if a contract slips. Tests run the same schemas in
// strict mode (Schema.parse on the backend side) so the gate is at PR
// time, not at the user's screen.
const validateShape = (label: string, schema: z.ZodTypeAny, data: unknown) => {
  const r = schema.safeParse(data)
  if (!r.success) {
    // eslint-disable-next-line no-console
    console.error(`[shared-api drift] ${label}`, r.error.issues)
  }
}

/*  GET /leaderboard — community leaderboard                          */

/** Up = rank improved (number got smaller). null when no snapshot yet. */
export type RankMovementLabel = 'up' | 'down' | 'same' | null

export type LeaderboardValidator = {
  id: string
  /** Server-supplied 1-based rank within the validator pool. */
  rank?: number
  /** Public-profile slug. Null only for legacy rows that haven't been backfilled. */
  handle?: string | null
  full_name: string | null
  points: number
  industry: string | null
  profession: string | null
  city: string | null
  rank_movement?: number | null
  rank_movement_label?: RankMovementLabel
}

/** Automated status badge attached to idea cards by the backend
 *  (badge.service.js). At most one per card — already resolved by priority —
 *  or null when the idea matches none. `key` is one of: expert_verified |
 *  hot | trending | closing_soon | needs_validators. */
export type IdeaBadge = { key: string; label: string }

export type ScoreBreakdown = {
  /** All percentages 0–100. Null when the section had too few rated answers. */
  problem: number | null
  solution: number | null
  market: number | null
  total_validators: number
  /** JSONB blob from reports.engagement — shape may grow over time. */
  engagement: {
    ideaReach?: number | null
    lostInterest?: number | null
    [k: string]: unknown
  } | null
}

export type LeaderboardIdea = {
  id: string
  title: string
  industry: string
  status: IdeaStatus
  overall_score: number
  number_of_upvotes: number
  number_of_downvotes?: number
  submitted_at: string
  review_opened_at?: string
  review_closed_at?: string | null
  submitted_by: { full_name: string; handle?: string | null } | null
  /** Live rank in the rankable-ideas list. Present on `my_ideas` rows. */
  idea_rank?: number | null
  rank_movement?: number | null
  rank_movement_label?: RankMovementLabel
  /** True when the caller submitted this idea. Server-supplied. */
  is_mine?: boolean
  /** Server-supplied breakdown for the score popover. Null if no report yet. */
  score_breakdown?: ScoreBreakdown | null
  /** Single automated status badge, or null. Server-supplied. */
  badge?: IdeaBadge | null
  /** Live top-level comment count. Attached by the leaderboard endpoint. */
  feedback_count?: number
  /** Live distinct-viewer count. Attached by the leaderboard endpoint. */
  view_count?: number
}

export type LeaderboardStats = {
  total_validators: number
  total_ideas_validated: number
  total_points_awarded: number
}

export type LeaderboardData = {
  stats: LeaderboardStats
  /** null when the caller hasn't completed any validations yet. */
  my_rank: number | null
  my_rank_movement?: number | null
  my_rank_movement_label?: RankMovementLabel
  /** Every rankable idea the caller submitted, regardless of top-N cutoff. */
  my_ideas?: LeaderboardIdea[]
  validators: LeaderboardValidator[]
  ideas: LeaderboardIdea[]
}

type LeaderboardResponse = {
  success: boolean
  data: LeaderboardData
}

export async function fetchLeaderboard(): Promise<LeaderboardData> {
  const { data } = await api.get<LeaderboardResponse>('/leaderboard')
  return data.data
}

/*  GET /leaderboard/validators — paginated slice                       */

export type ValidatorsPage = {
  validators: LeaderboardValidator[]
  total: number
  offset: number
  limit: number
}

export async function fetchValidatorsPage(
  offset: number,
  limit = 50,
): Promise<ValidatorsPage> {
  const { data } = await api.get<{ success: boolean; data: ValidatorsPage }>(
    '/leaderboard/validators',
    { params: { offset, limit } },
  )
  return data.data
}

/*  GET /leaderboard/me/dashboard — user dashboard                    */

export type IdeaStatus = 'live' | 'processed' | 'archived'

export type DashboardIdeaItem = {
  id: string
  title: string
  one_line_pitch: string
  industry: string
  status: IdeaStatus
  /** Owner visibility — drives the Public/Private toggle on dashboard cards. */
  is_private?: boolean
  overall_score: number
  number_of_upvotes: number
  number_of_downvotes?: number
  /** Per-viewer state — server-supplied via the dashboard endpoint. */
  has_upvoted?: boolean
  has_downvoted?: boolean
  submitted_at: string
  review_opened_at?: string
  review_closed_at?: string | null
  /** Review window length; with review_opened_at it gives the window's close
   * time. Optional for backward-compat — clients fall back to 7. */
  review_duration_days?: number
  submitted_by: { full_name: string; handle?: string | null } | null
  /** Live rank in the rankable-ideas list. Server-supplied on submitted ideas. */
  idea_rank?: number | null
  rank_movement?: number | null
  rank_movement_label?: RankMovementLabel
  /** Breakdown for the score popover. Null until the report cron has run. */
  score_breakdown?: ScoreBreakdown | null
  /** Single automated status badge, or null. Server-supplied. */
  badge?: IdeaBadge | null
  /** Live top-level comment count. Attached by the dashboard endpoint. */
  feedback_count?: number
  /** Live distinct-viewer count. Attached by the dashboard endpoint. */
  view_count?: number
}

export type DashboardData = {
  full_name: string
  points: number
  /** null when the user hasn't completed any validations yet. */
  rank: number | null
  rank_movement?: number | null
  rank_movement_label?: RankMovementLabel
  /** ISO timestamp of the caller's most recent completed validation; null when none. */
  last_validated_at?: string | null
  /** Distinct users (excluding self) who validated the caller's ideas. */
  community_validators?: number
  ideas_submitted: DashboardIdeaItem[]
  ideas_validated: DashboardIdeaItem[]
  /**
   * Ideas you deleted. They are still here and still restorable, and they are
   * deliberately absent from `ideas_submitted` so no count on the dashboard
   * treats them as live work.
   */
  ideas_deleted?: DashboardIdeaItem[]
}

type DashboardResponse = {
  success: boolean
  data: DashboardData
}

export async function fetchDashboard(): Promise<DashboardData> {
  const { data } = await api.get<DashboardResponse>('/leaderboard/me/dashboard')
  // Parse through the shared schema. If the backend response shape drifts,
  // tests fail with a precise "field X is missing/wrong type" message.
  validateShape('GET /leaderboard/me/dashboard', DashboardResponseSchema, data)
  return data.data
}

/*  GET /ideas/ — list                                                */

/** Slim card shape — also returned in homepage `top_ideas` and the ideas list. */
export type IdeaListItem = {
  id: string
  title: string
  one_line_pitch: string
  industry: string
  overall_score: number
  number_of_upvotes: number
  /** Optional for backward-compat; present on environments with the downvote BE. */
  number_of_downvotes?: number
  /** Per-viewer state, attached by list/leaderboard/dashboard endpoints. */
  has_upvoted?: boolean
  has_downvoted?: boolean
  submitted_at: string
  submitted_by: { full_name: string; handle?: string | null }
  /** Breakdown for the score popover. Null until the report cron has run. */
  score_breakdown?: ScoreBreakdown | null
  /** Single automated status badge, or null. Server-supplied. */
  badge?: IdeaBadge | null
  /** Live top-level comment count. Attached by the /ideas list endpoint. */
  feedback_count?: number
  /** Live distinct-viewer count. Attached by the /ideas list endpoint. */
  view_count?: number
  /** Lifecycle state — present on list responses that include status. */
  status?: IdeaStatus
  review_opened_at?: string | null
  review_closed_at?: string | null
  review_duration_days?: number | null
}

export type IdeasFacets = {
  industry: Record<string, number>
  status: Record<string, number>
}

type IdeasListResponse = {
  success: boolean
  data: {
    meta: {
      total_on_platform: number
      total_matching: number
      // total_visible_to_users is returned by the backend DTO but not yet in
      // the shared-api schema; safe to read since validateShape only logs.
      total_visible_to_users?: number
      facets: IdeasFacets
    }
    page: number
    total_pages: number
    ideas: IdeaListItem[]
  }
}

export type IdeasListResult = {
  total_on_platform: number
  total_matching: number
  total_visible_to_users?: number
  facets: IdeasFacets
  page: number
  total_pages: number
  ideas: IdeaListItem[]
}

/** UI-friendly sort tokens accepted by the backend. */
export type IdeasSort = 'all' | 'trending' | 'new' | 'ending_soon'

/**
 * Lifecycle filter for the feed. Alongside the real statuses, `closed` is a
 * server-side alias for processed + archived, so the Closed tab is one request
 * rather than two. Omitting it returns everything the board shows.
 */
export type IdeasStatusFilter = IdeaStatus | 'closed'

export type FetchIdeasOpts = {
  limit?: number
  page?: number
  industry?: string
  q?: string
  sort?: IdeasSort
  status?: IdeasStatusFilter
}

export async function fetchIdeas(
  opts: FetchIdeasOpts = {},
): Promise<IdeasListResult> {
  const { limit = 24, page = 1, industry, q, sort, status } = opts
  const params: Record<string, string | number> = { limit, page }
  if (industry) params.industry = industry
  if (q && q.trim()) params.q = q.trim()
  if (sort) params.sort = sort
  if (status) params.status = status
  const { data } = await api.get<IdeasListResponse>('/ideas/', { params })
  validateShape('GET /ideas/', IdeasListResponseSchema, data)
  const { meta, page: respPage, total_pages, ideas } = data.data
  return {
    total_on_platform: meta.total_on_platform,
    total_matching: meta.total_matching,
    total_visible_to_users: meta.total_visible_to_users,
    facets: meta.facets,
    page: respPage,
    total_pages,
    ideas,
  }
}

export type HomeData = {
  total_ideas_submitted: number
  ideas_submitted_this_week: number
  total_validators: number
  top_ideas: IdeaListItem[]
}

type HomeResponse = {
  success: boolean
  data: HomeData
}

export async function fetchHome(): Promise<HomeData> {
  const { data } = await api.get<HomeResponse>('/leaderboard/home')
  validateShape('GET /leaderboard/home', HomeResponseSchema, data)
  return data.data
}

/*  GET /ideas/:id — detail                                           */

export type MyValidationAnswer = {
  question_id: string
  question_text: string
  question_type: 'rating' | 'sentiment'
  rating: number | null
  sentiment: string | null
}

export type MyValidationReply = {
  id: string
  content: string
  author: { full_name: string }
  created_at: string
  upvotes: number
  has_liked: boolean
}

export type MyValidation = {
  id: string
  idea_id: string
  is_complete: boolean
  answers: MyValidationAnswer[]
  comment: {
    id: string
    content: string
    author: { full_name: string }
    created_at: string
    upvotes: number
    has_liked: boolean
    replies: MyValidationReply[]
  } | null
}

export type IdeaDetailData = {
  id: string
  title: string
  one_line_pitch: string
  problem_description: string
  problem_experienced_by: string
  problem_frequency: string
  urgency_level: number
  solution_description: string
  before_solution: string
  after_solution: string
  founders_confidence: number | null
  primary_audience: string
  specific_audience: string
  likelihood_to_pay: number
  existing_solutions: string
  known_competitors: string
  status: IdeaStatus
  review_opened_at: string
  review_closed_at: string | null
  archived_at: string | null
  review_duration_days: number
  /** 0-5 float — average of market-category rating answers. */
  market_score: number
  /** 0-5 float — average of problem-category rating answers. */
  problem_score: number
  /** 0-5 float — average of solution-category rating answers. */
  solution_score: number
  /** 0-5 float — average across all rating answers. */
  overall_score: number
  is_anonymous: boolean
  /** Owner-only visibility: when true the idea is off public discovery + leaderboard. */
  is_private: boolean
  industry: string
  number_of_upvotes: number
  /** Optional for backward-compat with environments not yet on the downvote BE. */
  number_of_downvotes?: number
  creator?: {
    full_name: string
    handle?: string | null
    id?: string
  } | null
  submitted_by?: { full_name: string; handle?: string | null } | null
  has_upvoted: boolean
  has_downvoted?: boolean
  my_validation: MyValidation | null
  validator_count: number
  feedback_count: number
  /** Live distinct-viewer count for the detail/validate page. */
  view_count?: number
  is_own_idea?: boolean
  /** Idea submission time — primary field on detail responses. */
  submitted_at?: string | null
  created_at?: string | null
  /** Present when the idea is on the leaderboard. */
  idea_rank?: number | null
}

type IdeaDetailResponse = {
  success: boolean
  data: IdeaDetailData
}

export async function fetchIdeaById(id: string): Promise<IdeaDetailData> {
  const { data } = await api.get<IdeaDetailResponse>(`/ideas/${id}`)
  validateShape('GET /ideas/:id', IdeaDetailResponseSchema, data)
  return data.data
}

/*  POST /ideas — create                                              */

/** Same strings as `PAY_LIKELIHOOD` on SubmitIdeaPage — maps selection to 1–5 for `likelihood_to_pay`. */
const PAY_LIKELIHOOD_ORDER = [
  'Users already paying',
  'Users would pay',
  'Potential to monetize',
  'Indirect monetisation',
  'Unclear monetisation',
] as const

/** Label for the option that does not collect a separate pricing note on the submit form. */
export const PAY_LIKELIHOOD_UNCLEAR = 'Unclear monetisation' as const

export function serializePayPriceRanges(
  ranges: PayPriceRangeRow[] | undefined,
): string {
  if (!ranges?.length) return ''
  return ranges
    .map(({ from, to }) => {
      const a = from.trim()
      const b = to.trim()
      if (!a || !b) return ''
      return `${a} to ${b}`
    })
    .filter(Boolean)
    .join('; ')
}

export type PayPriceRangeRow = { from: string; to: string }

export function payLikelihoodExpectsPricingField(
  value: string | null | undefined,
): boolean {
  return value != null && value !== PAY_LIKELIHOOD_UNCLEAR
}

function buildSpecificAudienceFromForm(form: SubmitIdeaFormState): string {
  const parts: string[] = []
  const audience = form.audienceDescription.trim()
  if (audience) parts.push(audience)
  const price =
    serializePayPriceRanges(form.payPriceRanges).trim() ||
    form.payPriceExpectation.trim()
  if (payLikelihoodExpectsPricingField(form.payLikelihood) && price) {
    parts.push(`Expected pricing: ${price}`)
  }
  return parts.join('\n\n').slice(0, 255)
}

/*  POST /validations/:id/comments — post a comment or reply         */

type PostCommentResponse = { success: boolean; data: { id: string } }

export async function postComment(
  ideaId: string,
  content: string,
  parentId?: string | null,
): Promise<string> {
  const { data } = await api.post<PostCommentResponse>(
    `/validations/${ideaId}/comments`,
    { content, parent_id: parentId ?? null },
  )
  return data.data.id
}

/*  GET /validations/:id/comments — full discussion thread for an idea  */

export type ValidationThreadComment = {
  id: string
  content: string
  parent_id: string | null
  author: { id: string; full_name: string | null } | null
  created_at: string | null
  upvotes: number
  has_liked: boolean
  replies: ValidationThreadComment[]
}

type ValidationCommentsResponse = {
  success: boolean
  data: ValidationThreadComment[]
}

export async function getValidationComments(
  ideaId: string,
): Promise<ValidationThreadComment[]> {
  const { data } = await api.get<ValidationCommentsResponse>(
    `/validations/${encodeURIComponent(ideaId)}/comments`,
  )
  validateShape(
    'GET /validations/:id/comments',
    CommentsListResponseSchema,
    data,
  )
  return data.data
}

/*  POST /validations/comments/:commentId/like — like/toggle like on a comment */

export async function likeComment(commentId: string): Promise<void> {
  await api.post(`/validations/comments/${encodeURIComponent(commentId)}/like`)
}

/*  PATCH /validations/comments/:commentId — author edits their comment */

export async function updateComment(
  commentId: string,
  content: string,
): Promise<void> {
  await api.patch(`/validations/comments/${encodeURIComponent(commentId)}`, {
    content,
  })
}

/*  DELETE /validations/comments/:commentId — author hard-deletes their comment */

export async function deleteComment(commentId: string): Promise<void> {
  await api.delete(`/validations/comments/${encodeURIComponent(commentId)}`)
}

/*  POST /validations/comments/:commentId/report — flag a comment for moderation */

/** Reasons accepted by the BE — keep in sync with comment-report.service.js. */
export type CommentReportReason =
  | 'spam'
  | 'harassment'
  | 'off_topic'
  | 'misinformation'
  | 'other'

export type CommentReportResult = {
  reported: boolean
  /** True when this user already had a prior report on this comment.
   *  The BE treats re-reports as idempotent so the FE can show the same
   *  success state either way. */
  already: boolean
}

type CommentReportResponse = {
  success: boolean
  data: CommentReportResult
}

export async function reportComment(
  commentId: string,
  reason: CommentReportReason,
  details?: string,
): Promise<CommentReportResult> {
  const { data } = await api.post<CommentReportResponse>(
    `/validations/comments/${encodeURIComponent(commentId)}/report`,
    { reason, details: details?.trim() || undefined },
  )
  return data.data
}

/*  POST /ideas/:id/upvote — toggle upvote                            */

type UpvoteResponse = {
  success: boolean
  /** `downvoted` always echoes `false` from the BE — kept symmetrical so the
   * client can reset its local downvote flag in the same code path. */
  data: { upvoted: boolean; downvoted?: boolean }
}

export async function toggleUpvote(ideaId: string): Promise<boolean> {
  const { data } = await api.post<UpvoteResponse>(`/ideas/${ideaId}/upvote`)
  validateShape('POST /ideas/:id/upvote', ToggleUpvoteResponseSchema, data)
  return data.data.upvoted
}

/*  POST /ideas/:id/downvote — toggle downvote (mutually exclusive with upvote) */

type DownvoteResponse = {
  success: boolean
  data: { downvoted: boolean; upvoted?: boolean }
}

export async function toggleDownvote(ideaId: string): Promise<boolean> {
  const { data } = await api.post<DownvoteResponse>(`/ideas/${ideaId}/downvote`)
  return data.data.downvoted
}

/*  PATCH /ideas/:id/privacy — owner toggles an idea private/public  */

export async function setIdeaPrivacy(
  ideaId: string,
  isPrivate: boolean,
): Promise<void> {
  await api.patch(`/ideas/${ideaId}/privacy`, { is_private: isPrivate })
}

/*  DELETE /ideas/:id — owner soft-deletes their idea at any stage   */

export async function deleteIdea(ideaId: string): Promise<void> {
  await api.delete(`/ideas/${ideaId}`)
}

/*  POST /ideas/:id/restore: owner brings a deleted idea back       */

export async function restoreIdea(ideaId: string): Promise<void> {
  await api.post(`/ideas/${ideaId}/restore`)
}

/*  POST /validations/:ideaId/submit — submit validation              */

export type ValidationAnswer = {
  question_id: string
  rating?: number | null
  sentiment?: string | null
}

export type SubmitValidationBody = {
  answers: ValidationAnswer[]
  /** Optional — the discussion thread (CommentThread) is now the primary way
   *  to comment; kept for back-compat with any caller still sending one. */
  comment?: string
}

export type SubmitValidationResult = {
  validation: {
    id: string
    idea_id: string
    validator_id: string
    comment: string
    is_complete: boolean
    updatedAt: string
    createdAt: string
  }
  newMarketScore: number
}

type SubmitValidationResponse = {
  success: boolean
  data: SubmitValidationResult
}

export async function submitValidation(
  ideaId: string,
  body: SubmitValidationBody,
): Promise<SubmitValidationResult> {
  const { data } = await api.post<SubmitValidationResponse>(
    `/validations/${ideaId}/submit`,
    body,
  )
  return data.data
}

/*  POST /ideas — create                                              */

export type CreateIdeaRequestBody = {
  title: string
  one_line_pitch: string
  problem_description: string
  problem_experienced_by: string
  problem_frequency: string
  urgency_level: number
  solution_description: string
  before_solution: string
  after_solution: string
  founders_confidence: number
  primary_audience: string
  specific_audience: string
  likelihood_to_pay: number
  existing_solutions: string
  known_competitors: string
  industry: string
  is_anonymous: boolean
  /** When true the idea is visible only to the owner and people they share it with. */
  is_private: boolean
}

/**
 * The submit form now collects competitors as one free-text field. Derive the
 * legacy `existing_solutions` label + `known_competitors` names from it so the
 * POST /ideas payload (and therefore the backend + report inputs) stays exactly
 * the same shape — both values remain part of the existing competitor vocabulary.
 */
function deriveCompetitorFields(competitorList: string): {
  existing_solutions: string
  known_competitors: string
} {
  const raw = competitorList.trim()
  const noneish = /^(none|no|n\/?a|nil|na|-)\.?$/i.test(raw)
  if (!raw || noneish) {
    return { existing_solutions: 'No known competitors', known_competitors: '' }
  }
  return { existing_solutions: 'Some competitors', known_competitors: raw }
}

function payLikelihoodToNumber(value: string | null): number {
  if (!value) return 1
  const i = PAY_LIKELIHOOD_ORDER.indexOf(
    value as (typeof PAY_LIKELIHOOD_ORDER)[number],
  )
  return i >= 0 ? i + 1 : 1
}

export function submitIdeaFormToCreateBody(
  form: SubmitIdeaFormState,
): CreateIdeaRequestBody {
  return {
    title: form.ideaTitle.trim() || 'Untitled idea',
    one_line_pitch: form.oneLinePitch.trim(),
    problem_description: form.problemWhat.trim(),
    problem_experienced_by: form.problemWho.trim(),
    problem_frequency: form.problemFrequency?.trim() ?? '',
    urgency_level: (form.urgencyLevel ?? 0) > 0 ? form.urgencyLevel! : 1,
    solution_description: form.solutionDescription.trim(),
    before_solution: form.beforeState.trim(),
    after_solution: form.afterState.trim(),
    founders_confidence: form.founderConfidence ?? 1,
    primary_audience: form.targetAudience.length
      ? form.targetAudience.join(', ')
      : '',
    specific_audience: buildSpecificAudienceFromForm(form),
    likelihood_to_pay: payLikelihoodToNumber(form.payLikelihood),
    ...deriveCompetitorFields(form.competitorList),
    industry: form.industry.trim() || 'Other',
    is_anonymous: form.submitAnonymously,
    is_private: form.submitPrivately,
  }
}

/*  GET /validations/questions — validation rating questions           */

export type ValidationQuestion = {
  id: string
  question_text: string
  order: number
  question_type?: 'rating' | 'sentiment'
  category: 'problem' | 'solution' | 'market' | 'sentiment'
}

type ValidationQuestionsResponse = {
  success: boolean
  data: ValidationQuestion[]
}

export async function fetchValidationQuestions(): Promise<
  ValidationQuestion[]
> {
  const { data } = await api.get<ValidationQuestionsResponse>(
    '/validations/questions',
  )
  return data.data.sort((a, b) => a.order - b.order)
}

/*  POST /ideas — create                                              */

/** POST `/ideas` (with axios `baseURL` e.g. `/api/v1` → `/api/v1/ideas`). */
export async function createIdea(body: CreateIdeaRequestBody) {
  const { data } = await api.post<unknown>('/ideas', body)
  return data
}

/** Best-effort id from POST /ideas envelopes (`{ data: { id } }`, `{ id }`, etc.). */
export function parseCreatedIdeaIdFromResponse(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const root = data as Record<string, unknown>
  const inner =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : root
  const id = inner.id
  return typeof id === 'string' && id.length > 0 ? id : null
}

/*  GET /leaderboard/me/points — current user's point transactions    */

export type PointReason =
  | 'idea_submitted'
  | 'idea_validated'
  | 'comment_left'
  | 'upvote_received'

export type PointTransaction = {
  id: string
  user_id: string
  points: number
  reason: PointReason
  reference_id: string | null
  created_at: string
  updated_at: string
}

type PointsHistoryResponse = {
  success: boolean
  data: PointTransaction[]
}

export async function fetchMyPoints(): Promise<PointTransaction[]> {
  const { data } = await api.get<PointsHistoryResponse>(
    '/leaderboard/me/points',
  )
  return data.data
}

/*  GET /reports/:id/report — idea validation report                  */

/** Backend mapper shapes this to match `MOCK_REPORT_DATA` in
 * `src/pages/IdeaReportPage.tsx`. Keep in sync with
 * `soft_launch_backend/src/mappers/report.mapper.js`. */

export type ReportBand = 'Strong' | 'Moderate' | 'Weak' | 'Insufficient data'
export type InsightTone = 'positive' | 'warning' | 'critical'
export type CommentSentiment = 'positive' | 'neutral' | 'negative'

export type ReportScoreCard = {
  value: number | null
  label: ReportBand
  rank?: number | null
  rankScope?: string | null
}

export type ReportEngagementCard = {
  value: number
  label: ReportBand
}

export type ReportTabCard = {
  validationCard: {
    title: string
    scorePercent: number | null
    bullets: string[]
  }
}

export type ReportCommentReply = {
  id: string
  user: string
  initials: string
  time: string
  text: string
  upvotes: number
  has_liked?: boolean
  createdAt: string | null
}

export type ReportComment = {
  id: string
  user: string
  initials: string
  time: string
  text: string
  upvotes: number
  has_liked?: boolean
  sentiment: CommentSentiment
  createdAt: string | null
  replies?: ReportCommentReply[]
}

export type ReportInsight = { text: string; tone: InsightTone }

export type CommentThemeSentiment = 'positive' | 'mixed' | 'negative'

/** A cluster of the validator comment thread, from the report.comment-themes
 * AI template. */
export type ReportCommentTheme = {
  theme: string
  sentiment: CommentThemeSentiment
  mentions: number
  summary: string
}

/** Market read from the report.market-assessment AI template (Gemini knowledge
 * plus similar JOMS ideas). */
export type ReportMarketAssessment = {
  summary: string
  positioning: string
  comparables: { name: string; note: string }[]
  risks: string[]
  opportunities: string[]
}

export type ReportVoterDistribution = {
  ageGroup: Record<string, number>
  city: Record<string, number>
  industry: Record<string, number>
  profession: Record<string, number>
}

export type IdeaReportFormAnswersPayload = {
  problemWhat: string
  problemWho: string
  problemFrequency: string
  urgencyLevel: number | null
  solutionDescription: string
  beforeState: string
  afterState: string
  founderConfidence: number | null
  targetAudience: string[]
  audienceDescription: string
  payLikelihood: string
  payPriceExpectation?: string
  competitors: string
  competitorList: string
}

export type IdeaReport = {
  ideaTitle: string
  /** Optional for backward-compat; older deployments may not return it. */
  oneLinePitch?: string
  isNew: boolean
  status: IdeaStatus
  reviewOpenedAt: string | null
  reviewDurationDays: number | null
  /** Live distinct-viewer count for the header. Optional for backward-compat. */
  viewCount?: number
  meta: { submittedAgo: string; votes: number; feedbackCount: number }
  scores: {
    overall: ReportScoreCard
    problem: ReportScoreCard
    solution: ReportScoreCard
    market: ReportScoreCard
  }
  engagement: {
    ideaReach: ReportEngagementCard
    validationCount: ReportEngagementCard
    lostInterest: ReportEngagementCard
  }
  formAnswers: IdeaReportFormAnswersPayload
  tabs: {
    problem: ReportTabCard
    solution: ReportTabCard
    market: ReportTabCard
  }
  trend7d: { day: string; score: number }[]
  aiInsights: ReportInsight[]
  /** Optional for backward-compat; older deployments may not return them. */
  commentThemes?: ReportCommentTheme[]
  marketAssessment?: ReportMarketAssessment | null
  voterDistribution: ReportVoterDistribution
  comments: ReportComment[]
  generatedAt: string | null
  scoringMode: 'weighted' | 'simple' | null
}

type IdeaReportResponse = {
  success: boolean
  data: IdeaReport
}

export async function getIdeaReport(ideaId: string): Promise<IdeaReport> {
  const { data } = await api.get<IdeaReportResponse>(
    `/reports/${ideaId}/report`,
  )
  return data.data
}
